import React from 'react';
import { ListboxOption } from '@headlessui/react';
import clsx from 'clsx';
import PlayIcon from '@/shared/icons/PlayIcon';
import DeleteIcon from '@/shared/icons/DeleteIcon';
import TruncatedTooltip from '@/v3-dashboard/components/UI/TruncatedTooltip';

/**
 * SelectOption - Individual option component for Select dropdown
 */
const SelectOption = ({
  option,
  index,
  selectedValue,
  playingId,
  onPlaySound,
  onOptionClick,
  customTriggerValue,
  optionContainerClassName,
  selectClassName,
}) => {
  const optionValue = option.value || option;
  const optionLabel = option.label || option;
  const isSelected = selectedValue === optionValue;
  const isPlaying = playingId === index;
  const isDisabled = option.disabled;
  const isCustomTrigger = customTriggerValue && optionValue === customTriggerValue;

  const handleMouseDown = (e) => {
    if (isDisabled) return;
    
    // For custom trigger, we want to handle the click and potentially prevent 
    // the Listbox from closing immediately if needed, or just trigger our action.
    if (isCustomTrigger) {
      e.preventDefault();
      e.stopPropagation();
      
      if (onOptionClick) {
        onOptionClick(optionValue);
      }
    }
  };

  const handleClick = (e) => {
    if (isDisabled) return;
    
    // For non-custom triggers, Listbox handles the selection.
    // But we still want to notify our parent if they want to know about every click.
    if (!isCustomTrigger && onOptionClick) {
      onOptionClick(optionValue);
    }
  };

  return (
    <ListboxOption
      value={optionValue}
      disabled={isDisabled}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      className={clsx(
        'group',
        'relative',
        'select-none',
        'py-v3-xs',
        'text-black',
        'flex',
        'items-center',
        'px-v3-xs',
        optionContainerClassName || 'justify-between',
        isDisabled && 'cursor-not-allowed text-v3-black-inactive',
        !isDisabled && 'cursor-pointer hover:bg-v3-menu-selected',
        isSelected && 'bg-v3-menu-selected',
        selectClassName
      )}
      data-testid={`select-option-${optionValue}`}
    >
      <span className="text-v3-h10 font-medium text-v3-black-max tracking-tight w-max max-w-[99%] flex items-center gap-v3-xs truncate">
        {option.imageUrl && (
          <img
            src={option.imageUrl}
            alt={optionLabel}
            className="w-6 h-6 rounded-full object-cover flex-shrink-0"
          />
        )}
        <TruncatedTooltip
          content={optionLabel}
          position="right"
        >
          <span className="truncate block">
            {optionLabel}
          </span>
        </TruncatedTooltip>
      </span>

      <div className="flex items-center gap-v3-xs">
        {/* Play Button */}
        {option.soundUrl && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPlaySound(option.soundUrl, index);
            }}
            className={clsx(
              'flex',
              'justify-center',
              'items-center',
              'size-7',
              'rounded-full',
              'transition-all',
              'duration-200',
              isPlaying 
                ? 'bg-v3-purple-primary' 
                : 'bg-transparent hover:bg-v3-black-border'
            )}
          >
            <PlayIcon
              className={clsx(
                'h-4 w-4',
                isPlaying ? 'text-white' : 'text-v3-black-secondary'
              )}
            />
          </button>
        )}

        {/* Delete Button */}
        {option.showDelete && option.onDelete && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              option.onDelete();
            }}
            className={clsx(
              'flex',
              'justify-center',
              'items-center',
              'size-7',
              'rounded-full',
              'bg-transparent',
              'hover:bg-v3-red-lighter',
              'transition-all',
              'duration-200'
            )}
          >
            <DeleteIcon className="h-4 w-4 text-v3-red-dark" />
          </button>
        )}
      </div>
    </ListboxOption>
  );
};

export default SelectOption;
